import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { colors } from "../../styles/style";
import {
  FeatureKey,
  FEATURE_LABELS,
  PRO_REQUIRED_EVENT,
} from "../../common/plan";
import { useBabyContext } from "../../context/BabyContext";

/**
 * The one place a refused Pro write is explained.
 *
 * Free families can open every page and see everything already saved there;
 * only adding and changing needs Pro. Rather than teach every page about 402s,
 * the API client announces a refusal and this notice answers it — with a way to
 * upgrade for the baby's admin, and who to ask for everyone else.
 */
export const ProRequiredNotice: React.FC = () => {
  const { isBabyAdmin, babyAdminName } = useBabyContext();
  const navigate = useNavigate();
  const [feature, setFeature] = useState<FeatureKey | null | undefined>(
    undefined
  );

  useEffect(() => {
    const onRefused = (event: Event) =>
      setFeature((event as CustomEvent).detail?.feature ?? null);
    window.addEventListener(PRO_REQUIRED_EVENT, onRefused);
    return () => window.removeEventListener(PRO_REQUIRED_EVENT, onRefused);
  }, []);

  if (feature === undefined) return null;

  const close = () => setFeature(undefined);
  const what = feature ? FEATURE_LABELS[feature] : "This";
  const canBuy = isBabyAdmin !== false;

  return (
    <Overlay onClick={close}>
      <Panel
        role="dialog"
        aria-label="Part of Pro"
        onClick={(event) => event.stopPropagation()}
      >
        <Title>{what} is part of Pro</Title>
        <Text>
          {canBuy
            ? "Everything you have already added stays here for you. Upgrade to Pro to keep adding to it."
            : babyAdminName
              ? `Everything already here stays. ${babyAdminName} can upgrade this baby to Pro so everyone can keep adding.`
              : "Everything already here stays. Whoever set this baby up can upgrade it to Pro so everyone can keep adding."}
        </Text>
        {canBuy && (
          <PrimaryButton
            type="button"
            onClick={() => {
              close();
              navigate("/settings");
            }}
          >
            See Pro
          </PrimaryButton>
        )}
        <TextButton type="button" onClick={close}>
          {canBuy ? "Not now" : "OK"}
        </TextButton>
      </Panel>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0 16px calc(32px + env(safe-area-inset-bottom, 0px));
  background-color: rgba(0, 0, 0, 0.35);
`;

const Panel = styled.div`
  width: min(340px, 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 28px 24px 20px;
  border-radius: 32px;
  background-color: ${colors.white};
  text-align: center;
`;

const Title = styled.h2`
  margin: 0;
  color: ${colors.darkBrown};
  font-size: 20px;
  font-weight: 500;
`;

const Text = styled.p`
  margin: 0;
  color: ${colors.warmthBrown};
  font-size: 14px;
  line-height: 20px;
`;

const PrimaryButton = styled.button`
  margin-top: 4px;
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 39px;
  background-color: ${colors.softBrown};
  color: ${colors.white};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
`;

const TextButton = styled.button`
  border: none;
  background: none;
  color: ${colors.warmthBrown};
  font-size: 14px;
  padding: 4px;
  cursor: pointer;
`;
